import { memo, useContext, useEffect } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import ObjectRelationshipCard from "./ObjectRelationshipCard";
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import Typography from '@mui/joy/Typography';
import { gradientTint } from "@utils/gradientTint";
import { sortByDate } from "@utils/sorters";
import { formatDate } from "@utils/dates";
import { DynamicControl } from "@components/forms/DynamicControl";
import { columnNameToLabel, titleCase } from "@utils/strings";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import SearchSelect from "@components/SearchSelect";
import { lookupCityProvince } from "@utils/lookupCityProvince";
import { useDebounce } from "@uidotdev/usehooks";
import { useTenant } from "@providers/TenantProvider";

export const ObjectPrimaryInfoEdit = memo(function ObjectPrimaryInfoEdit({
    cardHeader = null,
    tint,
    title,
    hideTitle = false,
    fields = [],
    ...restProps
}) {
    // const data = useContext(ObjectContext);
    const { getValues, setValue, register, control, watch, getFieldState } = useFormContext();

    // globally handle zipcode lookup
    const { tenant } = useTenant();
    const zipWatch = useWatch({name: ['zip_postal', 'zip_postal_mail']});
    const debouncedZip = useDebounce(zipWatch?.[0] ?? '', 600);
    const debouncedZipMail = useDebounce(zipWatch?.[1] ?? '', 600);
    // todo: implement getFieldState to ensure this only updates on change
    // https://react-hook-form.com/docs/useform/getfieldstate

    //const { touchedFields } = getFieldState("test", formState)


    const lookupZip = (suffix = '') => {
      // only check if data has been provided
      const currentState = getFieldState(`zip_postal${suffix}`);
      if(!currentState.isDirty) {
        return;
      }

      const values = getValues();
      const country = !!values[`country${suffix}`] ? values[`country${suffix}`] : tenant.country;
      const postal = !!values[`zip_postal${suffix}`] ? values[`zip_postal${suffix}`] : tenant.postal;

      lookupCityProvince(country, postal).then(response => {
        if(response.data.cities.length) {
          const result = response.data.cities[0];
          setValue(`city${suffix}`, result.name);
          setValue(`state_province${suffix}`, result.admin1_name);
        }
      });
    }

    useEffect(() => {
      if(debouncedZip) {
        lookupZip();
      }
    }, [debouncedZip]);

    useEffect(() => {
      if(debouncedZipMail) {
        lookupZip('_mail');
      }
    }, [debouncedZipMail]);

    if(
      restProps.hasOwnProperty('visible')
      && !restProps.visible(watch())
    ) {
      return null;
    }

    return (
    <ObjectRelationshipCard
      unpadded
      title={!hideTitle && (
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 1.5,
            })}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={0.5}
            >
              <Typography sx={theme => ({flex: 1, fontWeight: '600', color: 'white' })}>{title}</Typography>
            </Stack>
          </CardActions>
        </CardOverflow>
      )}
      sx={{
        '--mm-card-height': 'auto',
        ...(restProps.sx ?? {}),
      }}
    >
      {cardHeader != null && cardHeader}
      <Table
        // stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
        }}
      >
        <tbody>
        {fields.map((row, rowIndex) => {
          if(row.hasOwnProperty('conditional')) {
            if(typeof row.conditional === 'boolean' && typeof row.content !== 'string') {
                return <em>row content cannot be a function with a simple truthy conditional</em>
            }

            // general truthy check
            if(
                typeof row.conditional === 'boolean'
                && row.conditional === true
                && !getValues(row.content)
            ) {
                return null;
            }

            // fn truthy check
            if(
                typeof row.conditional === 'function'
                && row.conditional(watch()) !== true
            ) {
                return null;
            }
          }

          const label = typeof row.label === 'undefined' ? columnNameToLabel(row.name) : row.label;



          // asterisks are handled this way to keep the layout from shifting
          const hasAsterisk = !!(row?.config?.required) || row.hasOwnProperty('showRequired');
          const showAsterisk = !!(row?.config?.required) || row.hasOwnProperty('showRequired') && row.showRequired(getValues());
          return (
            <tr key={rowIndex}>
              {row.label !== false && (
                <td className="min">
                  {/* <label htmlFor={row.name}>{row.label}</label> */}
                  <Typography
                    component={'label'}
                    htmlFor={row.name}
                    fontSize={'sm'}
                    fontWeight={'bold'}
                  >
                    {label}{hasAsterisk && <Typography color="danger" fontSize={'xs'} sx={{position: 'relative', left: '.13rem', top: '-0.25rem', opacity: showAsterisk ? 1 : 0}}>*</Typography>}
                  </Typography>
                </td>
              )}
              <td className="max" colSpan={row.label !== false ? 1 : 2}>
                {row.type === 'info' && (<>
                  {/* {typeof row.content === 'function' ? row.content(watch()) : row.content} */}
                </>)}
                {row.type !== 'info' && (<DynamicControl key={`primary-edit-${rowIndex}`} {...row} />)}
                {typeof row.content === 'function' ? row.content(watch(), setValue, watch) : row.content}
              </td>


              {/* <ErrorMessage errors={errors} name={d.name} /> */}
            </tr>
          )
        })}
        </tbody>
      </Table>
    </ObjectRelationshipCard>
  );
});
